import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import * as _ from "lodash";
import { Document } from "domain/entities/document";
import { Employee } from "domain/entities/employee";
import { Invoice } from "domain/entities/invoice";
import { DocumentType } from "domain/entities/document-type";
import { InvoiceStatusEnum } from "domain/enum/invoiceStatus";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { httpClient } from "infra/axios/axios-api";
import { useDeleteCPSDocumentMutation, useDocumentByFilter } from "presentation/hooks/use-query-document";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import RegisterInvoice from "./components/RegisterInvoice";
import InvoiceSkeleton from "./Skeleton";
import MobileS3PDFViewerDialog from "presentation/mobile/components/MobileS3PDFViewerDialog";
import MobileImageViewerDialog from "presentation/mobile/components/MobileImageViewerDialog";
import usePermissionCheck from "presentation/hooks/use-check-permissions";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileFilterTabs from "presentation/mobile/components/MobileFilterTabs";
import { useStatusFilter } from "presentation/mobile/hooks/useStatusFilter";
import { INVOICE_STATUS_FILTERS } from "presentation/mobile/utils/status-filters.util";
import MobileActionButton from "presentation/mobile/components/MobileActionButton";
import MobileToast from "presentation/mobile/components/MobileToast";
import MobileRefreshingIndicator from "presentation/mobile/components/MobileRefreshingIndicator";
import MobileRefreshingList from "presentation/mobile/components/MobileRefreshingList";
import { getApiErrorMessage } from "presentation/mobile/utils/api-error.util";
import { INVOICE_STATUS_TONES, getStatusStyle } from "presentation/mobile/utils/status-styles.util";

const FUNCTION = "invoices";
const REQUIRE_FUNCTION = "invoices";

const getInvoiceStatus = (document: Document) => document.invoice?.status;

interface InvoiceItemProps {
    document: Document;
    isExpanded: boolean;
    onToggle: () => void;
    onView: (doc: Document) => void;
    onCancel: (doc: Document) => void;
}

const InvoiceItem: React.FC<InvoiceItemProps> = ({
    document,
    isExpanded,
    onToggle,
    onView,
    onCancel,
}) => {
    // Gestos: Long-press ou clique simples no cartão alternam a expansão
    const { isPressing, handlers } = useLongPress({
        onLongPress: onToggle,
    });

    const invoice = document.invoice;

    const getPeriod = () => {
        if (!invoice?.month || !invoice?.year) return "-";
        const m = String(invoice.month).padStart(2, "0");
        return `${m}/${invoice.year}`;
    };

     const statusInfo = {
        label: invoice?.status ?? "Pendente",
        ...getStatusStyle(INVOICE_STATUS_TONES, invoice?.status),
    };

    const canCancel = invoice?.status === InvoiceStatusEnum.ENVIADO;

    return (
        <div
            {...handlers}
            className="border-round-xl p-3 flex flex-column gap-2 cursor-pointer"
            style={{
                backgroundColor: "#FFFFFF",
                border: isExpanded ? "1.5px solid #0084D1" : "1px solid #E2E8F0",
                boxShadow: isExpanded ? "0 4px 14px rgba(0, 132, 209, 0.08)" : "none",
                transform: isPressing ? "scale(0.99)" : "scale(1)",
                transition: "all 0.2s ease",
            }}
        >
            {/* Linha 1: Título com ponto azul se expandido + Badge de Estado + Botão '+' / 'x' */}
            <div className="w-full flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-1 overflow-hidden pr-2">
                    {isExpanded && (
                        <span
                            style={{
                                color: "#0084D1",
                                fontSize: "1.1rem",
                                lineHeight: "1",
                                marginRight: "2px",
                            }}
                        >
                            •
                        </span>
                    )}
                    <span className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {`${(document.documentType as DocumentType)?.name ?? "Fatura"} - ${(document.employee as Employee)?.name ?? ""
                            }`}
                    </span>
                </div>

                <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                        className="badge-squircle"
                        style={{
                            color: statusInfo.color,
                            backgroundColor: statusInfo.bg,
                            border: `1px solid ${statusInfo.border}`,
                        }}
                    >
                        {statusInfo.label}
                    </span>

                    {/* Micro-interação: '+' gira 45º para 'x' */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        className="border-none bg-transparent cursor-pointer flex align-items-center justify-content-center p-0"
                        style={{
                            width: "24px",
                            height: "24px",
                            color: "#94A3B8",
                            outline: "none",
                        }}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                    >
                        <i
                            className="pi pi-plus text-xs"
                            style={{
                                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Linha 2: Período */}
            <div className="w-full">
                <span className="text-xs" style={{ color: "#64748B" }}>
                    Período: <strong style={{ color: "#0F172A" }}>{getPeriod()}</strong>
                </span>
            </div>

            {/* Secção Expandida: APENAS OS BOTÕES DE AÇÃO */}
            {isExpanded && (
                <div
                    className="w-full flex flex-column gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px solid #F1F5F9" }}
                >
                    {/* Motivo de recusa exibido apenas se houver */}
                    {invoice?.rejectionReason && (
                        <div
                            className="w-full p-2 border-round-md"
                            style={{ backgroundColor: "#FEF2F2", border: "1px solid #FEE2E2" }}
                        >
                            <span className="text-xs font-semibold text-red-700">
                                Motivo da não aprovação:{" "}
                            </span>
                            <span className="text-xs text-red-600">
                                {invoice.rejectionReason}
                            </span>
                        </div>
                    )}

                    {/* Botões de Ação alinhados à direita */}
                    {/* Botões de Ação alinhados à direita */}
                    <div className="w-full flex justify-content-end gap-2 pt-1">
                        {canCancel && (
                            <MobileActionButton
                                label="Anular"
                                icon="pi pi-trash"
                                severity="danger"
                                onClick={() => onCancel(document)}
                            />
                        )}
                        <MobileActionButton
                            label="Visualizar"
                            icon="pi pi-eye"
                            severity="secondary"
                            onClick={() => onView(document)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const Invoices = () => {
    const { isLoading, hasPermission } = usePermissionCheck({ requiredFunction: REQUIRE_FUNCTION });
    const navigationState = useNavigationStore();
    const { userEmployee } = useAuthStore();
    const toast = useRef<Toast>(null);


    const [lazyParams, setLazyParams] = useState({
        sortBy: "createdAt",
        sortOrder: "desc",
        limit: 50,
        page: 1,
        filters: {} as any,
    });

    const [invoices, setInvoices] = useState<Document[]>([]);
    const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);
    const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);
    const [registerModalVisible, setRegisterModalVisible] = useState<boolean>(false);

    const { mutateAsync: deleteCPSDocumentMutation } = useDeleteCPSDocumentMutation();

    const {
        data: documents,
        isLoading: isDocumentsLoading,
        isError,
        isRefetching,
        refetch,
    } = useDocumentByFilter({
        sortBy: lazyParams.sortBy,
        sortOrder: lazyParams.sortOrder,
        page: lazyParams.page,
        limit: lazyParams.limit,
        justCPSdocs: true,
        ...lazyParams.filters,
    } as DocumentInputFilter);

    useEffect(() => {
        if (navigationState.currentRoute !== FUNCTION) {
            navigationState.setCurrentRoute(FUNCTION);
        }

        setLazyParams((prev) => ({
            ...prev,
            filters: { ...prev.filters, employeeId: userEmployee?.id },
        }));
    }, [navigationState, userEmployee?.id]);

    useEffect(() => {
        if (!documents?.data || isDocumentsLoading) return;

        if (!_.isEqual(documents.data, invoices)) {
            setInvoices(documents.data);
        }
    }, [documents?.data, isDocumentsLoading, invoices]);

    const {
        activeTab,
        setActiveTab,
        filterTabs,
        filteredItems: filteredInvoices,
    } = useStatusFilter(invoices, INVOICE_STATUS_FILTERS, getInvoiceStatus);

    const confirmAction = (message: string, acceptCallback: () => void) => {
        confirmDialog({
            message,
            header: "Confirmação",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sim",
            rejectLabel: "Não",
            accept: acceptCallback,
        });
    };

    const removeDocument = async (docData: Document) => {
        if (!docData?.id || !docData.fullPath) return;

        try {
            await httpClient.delete(`/assets/invoices/${userEmployee?.id}`, {
                data: { filePath: docData.fullPath },
            });

            const currentDocument: Document = {
                id: docData.id,
                documentType: { id: docData.documentType.id },
                employee: { id: userEmployee?.id || "" },
                fullPath: docData.fullPath,
                lastModifiedBy: userEmployee?.emailAddress ?? "",
            };

            await deleteCPSDocumentMutation(currentDocument);
            toast.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Documento eliminado com sucesso.",
                life: 3500,
            });
        } catch (err: unknown) {
            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: getApiErrorMessage(err, "Documento não eliminado."),
                life: 3500,
            });
        }

    };

    const handleCancel = (document: Document) => {
        confirmAction("Deseja anular a submissão deste documento?", async () => {
            setInvoices((prev) => prev.filter((inv) => inv.id !== document.id));
            await removeDocument(document);
            await refetch();
        });
    };

    const handleView = (document: Document) => {
        if (document?.fullPath) {
            setS3PathToDisplay(document.fullPath);
        }
    };

    const handleToggle = (id: string) => {
        setExpandedInvoiceId((prev) => (prev === id ? null : id));
    };

    const isDocumentPdf = (path: string) => {
        const ext = /(?:\.([^.]+))?$/.exec(path);
        return ext && ext[1].toLowerCase() === "pdf";
    };

    const displayDocument = () => {
        if (!s3PathToDisplay) return null;

        if (isDocumentPdf(s3PathToDisplay)) {
            return (
                <MobileS3PDFViewerDialog
                    s3Path={s3PathToDisplay}
                    onHide={() => setS3PathToDisplay(null)}
                />
            );
        }
        return (
            <MobileImageViewerDialog
                s3Path={s3PathToDisplay}
                onHide={() => setS3PathToDisplay(null)}
            />
        );
    };

    if (isLoading || isDocumentsLoading) {
        return <InvoiceSkeleton />;
    }

    return (
        <>
            <div className="flex flex-column pt-2 px-3 gap-2 pb-8 align-items-center">
                {/*  ABAS DE FILTRO REUTILIZÁVEIS (MobileFilterTabs)  */}
                <MobileFilterTabs
                    tabs={filterTabs}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    scrollable
                />

                {/*  CABEÇALHO DA SECÇÃO  */}
                <div className="w-full flex justify-content-between align-items-center pt-1 px-1">
                    <span
                        className="font-semibold text-xs"
                        style={{ color: "#64748B" }}
                    >
                        As minhas faturas
                    </span>
                    {isRefetching && <MobileRefreshingIndicator/>}
                </div>

                {/*  LISTA DE CARTÕES EXPANSÍVEIS  */}
                <MobileRefreshingList
                    isRefreshing={isRefetching}
                    className="w-full flex flex-column gap-3">
                    {isError ? (
                        <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                            <i className="pi pi-exclamation-circle text-4xl text-red-500" />
                            <span className="text-sm text-center text-600">
                                Não foi possível carregar as faturas.
                            </span>
                        </div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                            <i className="pi pi-receipt text-4xl" style={{ color: "#c5c5c5" }} />
                            <span className="text-sm text-center" style={{ color: "#888" }}>
                                {activeTab !== "all"
                                    ? "Nenhuma fatura encontrada com este filtro."
                                    : "Não tem faturas registadas."}
                            </span>
                        </div>
                    ) : (
                        filteredInvoices.map((document) => (
                            <InvoiceItem
                                key={document.id}
                                document={document}
                                isExpanded={expandedInvoiceId === document.id}
                                onToggle={() => document.id && handleToggle(document.id)}
                                onView={handleView}
                                onCancel={handleCancel}
                            />
                        ))
                    )}
                </MobileRefreshingList>

                {/* Botão Flutuante Circular de Adicionar (+) */}
                <Button
                    icon="pi pi-plus"
                    rounded
                    className="fixed"
                    onClick={() => setRegisterModalVisible(true)}
                    style={{
                        bottom: "6rem",
                        right: "20px",
                        zIndex: 1000,
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#0084D1",
                        border: "none",
                    }}
                />
            </div>

            <ConfirmDialog />
            <MobileToast ref={toast} />

            <RegisterInvoice
                refetch={refetch}
                visible={registerModalVisible}
                setVisible={setRegisterModalVisible}
                toastRef={toast}
            />


            {s3PathToDisplay && displayDocument()}
        </>
    );
};

export default Invoices;